import { DiffMatchPatch, DiffTypes } from "@aderant/aderant-web-fw-core";
/**
 * Returns the items after itemsToDelete have been deleted from items
 * @param {DataProviderItem[]} items - the original items
 * @param {DataProviderItem[]} itemsToDelete - the items to remove from items
 * @returns {DataProviderItem[]} - the original items that don't exist in itemsToDelete
 */
export function deleteItemsFromList(items, itemsToDelete) {
    const newItems = items.filter((item) => {
        return itemsToDelete.every((itemToDelete) => item.id !== itemToDelete.id);
    });
    return newItems;
}
/**
 * Returns the items whose primaryText exists in the previous text but not in the current text
 * @param {DataProviderItem[]} items - the items to check for deletion
 * @param {string} prev - the previous text, before it was edited
 * @param {string} current - the current text, after it was edited
 * @returns {DataProviderItem[]} - the items whose primaryText has exists in the previous text but not in the current text
 */
export function getItemsToDelete(items, prev, current) {
    const itemsToDelete = [];
    for (let i = 0; i < items.length; i++) {
        const pos = prev.indexOf(items[i].primaryText);
        if (pos === -1) {
            continue; //the name was added not deleted
        }
        const pos2 = current.indexOf(items[i].primaryText);
        if (pos2 === -1) {
            //the name was deleted
            itemsToDelete.push(items[i]);
        }
    }
    return itemsToDelete;
}
/**
 * Deletes the edited version of the nameToDelete from the current text
 * @param {string} prev - the previous text, before it was edited
 * @param {string} current - the current text, after it was edited
 * @param {string} nameToDelete - the name that exists in prev but has been edited so that it no longer exists in current
 * @returns {string} - the current text with the {edited} nameToDelete removed
 */
function deleteNameFromCurrentText(prev, current, nameToDelete) {
    //If one or more letters have been removed from the name btween prev and current, find the changed name in current and delete it
    let result = current;
    const index = prev.indexOf(nameToDelete);
    if (index !== -1) {
        const prevBeforeName = prev.slice(0, index);
        const prevAfterName = prev.slice(index + nameToDelete.length);
        if (current.startsWith(prevBeforeName) && current.endsWith(prevAfterName)) {
            const newName = current.slice(prevBeforeName.length, prevAfterName ? current.indexOf(prevAfterName) : current.length);
            const dmp = new DiffMatchPatch();
            const diffs = dmp.diff_main(nameToDelete, newName);
            if (diffs.every((diff) => diff[0] === DiffTypes.DIFF_DELETE || diff[0] === DiffTypes.DIFF_EQUAL)) {
                //This means we have only deleted characters from the name - no inserts
                //We can remove the whole name from the current text
                result = current.substring(0, prevBeforeName.length) + (prevAfterName ? current.substring(current.indexOf(prevAfterName)) : "");
            }
        }
    }
    return result;
}
/**
 * Deletes the namesToDelete from the current text. NamesToDelete and prev contain the unedited version of the names to delete.
 * Current contains the edited version. This function deletes the edited version from current.
 * @param {string} prev - the previous text, before it was edited
 * @param {string} current - the current text, after it was edited
 * @param {string[]} namesToDelete - the names that exist in prev but have been edited so that they no longer exist in current
 * @returns {string} - the current text with the {edited} namesToDelete removed
 * */
export function deleteNamesFromCurrentText(prev, current, namesToDelete) {
    let result = current;
    for (let i = 0; i < namesToDelete.length; i++) {
        const nameToDelete = namesToDelete[i];
        result = deleteNameFromCurrentText(prev, result, nameToDelete);
    }
    return result;
}
